import { Category } from '@trackman/baseball-shared-components';
import { SUPPORT_PHONE_NUMBER } from 'constants/SupportPhoneNumber';
import { DeviceStatus } from 'enums/trackingElements/DeviceStatus';
import { DeviceType, DeviceTypeTitleDict } from 'enums/trackingElements/DeviceType';
import { DeviceTypeCategory } from 'enums/trackingElements/DeviceTypeCategory';
import { injectable } from 'inversify';
import { action, computed, makeAutoObservable } from 'mobx';
import { SessionStore } from './SessionStore';

@injectable()
export class TrackingElementsStore {
  statuses: DeviceStatus[] = [];

  private sessionStore: SessionStore;

  shouldTriggerNotification = true;

  updating = false;

  @computed get getErrors() {
    const errors: Pick<DeviceStatus, 'deviceType' | 'errorMessage'>[] = [];

    this.statuses.forEach((status) => {
      if (status.errorMessage && status.errorMessage.length > 0) {
        errors.push({ deviceType: status.deviceType, errorMessage: status.errorMessage });
      }
    });

    return errors;
  }

  constructor(sessionStore: SessionStore) {
    this.sessionStore = sessionStore;
    makeAutoObservable(this);
  }

  setUpdating = (value: boolean) => {
    this.updating = value;
  };

  setShouldTriggerNotification = (value: boolean) => {
    this.shouldTriggerNotification = value;
  };

  getStatuses = (category: DeviceTypeCategory) => {
    switch (category) {
      case DeviceTypeCategory.BallTracking:
        return this.statuses.filter((status) => status.deviceType === DeviceType.Ptu || status.deviceType === DeviceType.PtuCamera);
      case DeviceTypeCategory.Spin3dTracking:
        return this.statuses.filter((status) => status.deviceType === DeviceType.SpinHome);
      case DeviceTypeCategory.BatTracking:
        return this.statuses.filter(
          (status) =>
            status.deviceType === DeviceType.BatHome ||
            status.deviceType === DeviceType.BatLeft ||
            status.deviceType === DeviceType.BatRight
        );
    }
  };

  @action setStatuses = (statuses: DeviceStatus[]) => {
    const newStatuses: DeviceStatus[] = [];

    statuses.forEach((status) => {
      if (
        status.deviceType === DeviceType.BatHome ||
        status.deviceType === DeviceType.BatLeft ||
        status.deviceType === DeviceType.BatRight
      ) {
        if (this.sessionStore.currentSessionMetadata.isBatTrackingEnabled) {
          newStatuses.push(status);
        }

        return;
      }

      if (status.deviceType === DeviceType.PtuCamera) {
        if (this.sessionStore.currentSessionMetadata.isVideoCaptureEnabled) {
          newStatuses.push(status);
        }

        return;
      }

      if (status.deviceType === DeviceType.SpinHome) {
        if (this.sessionStore.currentSessionMetadata.is3DSpinEnabled) {
          newStatuses.push(status);
        }

        return;
      }

      newStatuses.push(status);
    });

    if (!newStatuses.some((status) => status.errorMessage && status.errorMessage.length > 0)) {
      this.setShouldTriggerNotification(true);
    }

    this.statuses = newStatuses;
  };

  getCategories = (): Category[] => {
    const categories = [
      {
        title: 'Ball Tracking',
        category: DeviceTypeCategory.BallTracking,
      },
      {
        title: '3d Spin Tracking',
        category: DeviceTypeCategory.Spin3dTracking,
      },
      {
        title: 'Bat Tracking',
        category: DeviceTypeCategory.BatTracking,
      },
    ].map((category) => {
      return {
        title: category.title,
        items: this.getStatuses(category.category).map((status) => {
          return {
            title: DeviceTypeTitleDict[status.deviceType],
            status: status.status,
            isError: !!status.errorMessage && status.errorMessage.length > 0,
            deviceType: status.deviceType,
          };
        }),
        errorMessage: `Please call support at ${SUPPORT_PHONE_NUMBER}.`,
      };
    });

    return categories;
  };
}
